/**
 * The toolbar's "column loop" dialog: makes the selected cells of one row a column loop, or renames or removes the loop
 * the cursor is in (cdm/columnLoopEdit.ts). The target is read when the dialog opens, so typing does not move it.
 */
import { useState, type FormEvent } from 'react'
import type { Editor } from '@tiptap/core'
import { Button, Modal } from '../ui'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import { applyColumnLoop, columnLoopEditError, columnLoopTarget, type ColumnLoopTarget } from '../../cdm/columnLoopEdit'
import { DEFAULT_ITEM_ALIAS } from '../../cdm/loopRunEdit'

export default function ColumnLoopDialog({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const [target] = useState<ColumnLoopTarget | null>(() => columnLoopTarget(editor))
  const [rawName, setRawName] = useState(target?.existing?.rawName ?? '')
  const [itemAlias, setItemAlias] = useState(target?.existing?.itemAlias ?? DEFAULT_ITEM_ALIAS)
  const error = target?.error ?? (target ? null : 'Put the cursor in a table cell first.')
  const editError = columnLoopEditError({ rawName, itemAlias })
  const done = () => { onClose(); editor.view.focus() }

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!target || error || editError) return
    applyColumnLoop(editor, target, { rawName, itemAlias })
    done()
  }

  const input = { padding: '6px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, fontFamily: 'monospace', width: '100%', boxSizing: 'border-box' as const }
  const label = { display: 'flex', flexDirection: 'column' as const, gap: 4, fontSize: fontSize.sm, color: colors.textSecondary }

  return (
    <Modal open onClose={done} width={420}
      title={target?.existing ? 'Column loop' : 'Make a column loop'}
      subtitle={target?.existing ? `${target.cells.length} column${target.cells.length === 1 ? '' : 's'} repeat for each item.` : 'The selected columns repeat once per item, in every row.'}
      footer={error ? <Button variant="primary" onClick={done}>OK</Button> : (
        <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end', width: '100%' }}>
          {target?.existing && (
            <Button variant="secondary" onClick={() => { applyColumnLoop(editor, target, null); done() }}>Remove loop</Button>
          )}
          <Button variant="secondary" onClick={done}>Cancel</Button>
          <Button variant="primary" type="submit" form="column-loop-form" disabled={editError !== null}>Apply</Button>
        </div>
      )}>
      {error ? (
        <div role="alert" style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>{error}</div>
      ) : (
        <form id="column-loop-form" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          <label style={label}>
            Repeat for each item of
            <input aria-label="Collection" autoFocus value={rawName} onChange={e => setRawName(e.target.value)} spellCheck={false} placeholder="months" style={input} />
          </label>
          <label style={label}>
            Item name
            <input aria-label="Item name" value={itemAlias} onChange={e => setItemAlias(e.target.value)} spellCheck={false} placeholder={DEFAULT_ITEM_ALIAS} style={input} />
          </label>
          {rawName !== '' && editError && <div role="alert" style={{ color: colors.error, fontSize: fontSize.xs }}>{editError}</div>}
          <div style={{ color: colors.textMuted, fontSize: fontSize.xs }}>
            In those columns, {'{{label}}'} or {`{{${itemAlias.trim() || DEFAULT_ITEM_ALIAS}.label}}`} reads the item. Word templates only.
          </div>
        </form>
      )}
    </Modal>
  )
}
